import { useCallback, useMemo } from 'react';
import useSWR from '../../../../../../swr/dist/core/index.js';
import { useStateWithDeps } from '../useStateWithDeps/useStateWithDeps.js';

const CUSTOMER_CONFIGURATION_API_SLOW_TIMEOUT = 1000;
const customProps = {
    isReady: 'isReady',
    isLoadingSlow: 'isLoadingSlow',
    revalidate: 'revalidate',
    customerConfiguration: 'customerConfiguration',
};
function useCustomerConfiguration({ caller, domain, token, customerId, initialCustomerConfiguration, getStateFromCustomerConfiguration, onLoadCustomerConfiguration, loadingTimeout = CUSTOMER_CONFIGURATION_API_SLOW_TIMEOUT, }) {
    const swrResponse = useSWR(token && customerId
        ? [
            'customer-configuration',
            customerId,
            caller,
            domain,
            JSON.stringify(initialCustomerConfiguration),
        ]
        : null, () => getCustomerConfig(token, domain, customerId, caller, initialCustomerConfiguration, getStateFromCustomerConfiguration, onLoadCustomerConfiguration), {
        fallbackData: initialCustomerConfiguration,
        loadingTimeout,
        onLoadingSlow: () => setCustomResponse({ isLoadingSlow: true }),
        onSuccess: () => setCustomResponse({ isLoadingSlow: false }),
        onError: () => setCustomResponse({ isLoadingSlow: false }),
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });
    const [customResponse, setCustomResponse, customResponseDependencies] = useStateWithDeps({
        isLoadingSlow: false,
    });
    const isReady = Boolean(token) && Boolean(customerId);
    const mutate = swrResponse.mutate;
    const revalidate = useCallback(() => mutate(), [mutate]);
    return useMemo(() => new Proxy(swrResponse, {
        get(target, prop, receiver) {
            if (prop === customProps.isReady) {
                return isReady;
            }
            if (prop === customProps.isLoadingSlow) {
                customResponseDependencies.isLoadingSlow = true;
                return customResponse.current.isLoadingSlow;
            }
            if (prop === customProps.revalidate) {
                return revalidate;
            }
            if (prop === customProps.customerConfiguration) {
                return target.data;
            }
            return Reflect.get(target, prop, receiver);
        },
        getOwnPropertyDescriptor() {
            return {
                enumerable: true,
                configurable: true,
            };
        },
        has(target, prop) {
            return [...Object.keys(customProps), ...Reflect.ownKeys(target)].includes(prop.toString());
        },
        ownKeys(target) {
            return [...Object.keys(customProps), ...Reflect.ownKeys(target)];
        },
    }), [isReady, revalidate, swrResponse, customResponse, customResponseDependencies]);
}
async function getCustomerConfig(token, domain, customerId, caller, initialCustomerConfiguration, getStateFromCustomerConfiguration, onLoadCustomerConfiguration) {
    try {
        const response = await fetch(`/myaccount/api/customer-config/v1/customerconfiguration/${customerId}`, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${token}`,
                'X-Original-Host': domain,
                'X-Caller': caller,
            },
            cache: 'no-store',
        });
        if (response.ok) {
            const customerConfig = await response.json();
            if (typeof onLoadCustomerConfiguration === 'function') {
                onLoadCustomerConfiguration(customerConfig, initialCustomerConfiguration);
            }
            return getStateFromCustomerConfiguration(customerConfig, initialCustomerConfiguration);
        }
        throw new Error(`Received customer configuration response with status code ${response.status}`);
    }
    catch (error) {
        return Promise.reject(error);
    }
}

export { useCustomerConfiguration };
//# sourceMappingURL=useCustomerConfiguration.js.map
